/**
 * SuperAdmin client-groups panel — list / create / rename / delete groups
 * used to organise managed broker accounts. Mounted on SuperAdminManagedUsersPage
 * below the manager/pool grid.
 *
 * Phase 1: CRUD only. Phase 2 adds the per-broker-account assignment + usage
 * guard on delete.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Pencil, Plus, RefreshCw, Tag, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useToast } from '../common/ToastContainer';
import clientGroupsService, { ClientGroup } from '../../services/clientGroupsService';

const ClientGroupsPanel: React.FC = () => {
  const toast = useToast();
  const [groups, setGroups] = useState<ClientGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create / edit dialog state
  const [editingGroup, setEditingGroup] = useState<ClientGroup | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Delete confirm
  const [pendingDelete, setPendingDelete] = useState<ClientGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchGroups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientGroupsService.listAdmin();
      setGroups(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load client groups');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openCreate = () => {
    setEditingGroup(null);
    setName('');
    setDescription('');
    setColor('');
    setDialogError(null);
    setCreateOpen(true);
  };

  const openEdit = (g: ClientGroup) => {
    setEditingGroup(g);
    setName(g.name);
    setDescription(g.description || '');
    setColor(g.color || '');
    setDialogError(null);
    setCreateOpen(true);
  };

  const closeDialog = () => {
    if (submitting) return;
    setCreateOpen(false);
    setEditingGroup(null);
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setDialogError('Name is required');
      return;
    }
    setSubmitting(true);
    setDialogError(null);
    try {
      if (editingGroup) {
        // Build the diff — only send fields that actually changed
        const diff: { name?: string; description?: string; color?: string } = {};
        if (trimmedName !== editingGroup.name) diff.name = trimmedName;
        if (description !== (editingGroup.description || '')) diff.description = description;
        if (color !== (editingGroup.color || '')) diff.color = color;
        if (Object.keys(diff).length === 0) {
          setDialogError('Nothing changed');
          setSubmitting(false);
          return;
        }
        await clientGroupsService.update(editingGroup.group_id, diff);
        toast.showSuccess(`Group "${trimmedName}" updated`);
      } else {
        await clientGroupsService.create({
          name: trimmedName,
          description: description || undefined,
          color: color || undefined,
        });
        toast.showSuccess(`Group "${trimmedName}" created`);
      }
      setCreateOpen(false);
      fetchGroups();
    } catch (err: any) {
      setDialogError(err.message || 'Failed to save group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await clientGroupsService.delete(pendingDelete.group_id);
      toast.showSuccess(`Group "${pendingDelete.name}" deleted`);
      setPendingDelete(null);
      fetchGroups();
    } catch (err: any) {
      toast.showError(err.message || 'Failed to delete group');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle>Client Groups ({groups.length})</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchGroups}
                    disabled={isLoading} title="Refresh">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
              New Group
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {error && (
          <div className="mx-6 my-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}
        {isLoading ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading…
          </div>
        ) : groups.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No client groups defined. Use <strong>New Group</strong> to create the first one.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {groups.map((g) => (
              <li key={g.group_id} className="px-6 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="inline-flex h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: g.color || '#9ca3af' }}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      <Tag className="inline-block h-3.5 w-3.5 mr-1 text-gray-400" />
                      {g.name}
                    </div>
                    {g.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {g.description}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(g)}
                          title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPendingDelete(g)}
                          title="Delete">
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {/* Create / Edit dialog */}
      <Modal isOpen={createOpen} onClose={closeDialog} maxWidth="md"
             ariaLabel={editingGroup ? 'Edit Group' : 'New Group'}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {editingGroup ? `Edit Group: ${editingGroup.name}` : 'New Client Group'}
          </h2>
          {dialogError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-800 dark:text-red-200">{dialogError}</span>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Name *
              </label>
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); setDialogError(null); }}
                placeholder="e.g. Premium, BFW, Beta-Cohort"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Description
              </label>
              <Input
                value={description}
                onChange={(e) => { setDescription(e.target.value); setDialogError(null); }}
                placeholder="Optional"
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color || '#9ca3af'}
                  onChange={(e) => { setColor(e.target.value); setDialogError(null); }}
                  disabled={submitting}
                  className="h-9 w-12 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer"
                />
                <Input
                  value={color}
                  onChange={(e) => { setColor(e.target.value); setDialogError(null); }}
                  placeholder="#9ca3af (optional)"
                  disabled={submitting}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={closeDialog} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} isLoading={submitting} disabled={submitting}>
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => !isDeleting && setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete Client Group"
        message={
          pendingDelete
            ? `Delete group "${pendingDelete.name}"? Phase 2 will block this if any broker account uses the group.`
            : ''
        }
        confirmText={isDeleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />
    </Card>
  );
};

export default ClientGroupsPanel;
